import { BrandConfig } from './types';
import { BASE_ROOT } from '@ringcentral/rwc-utils/src/url';
import { getLocale } from '@rwc/utils/locale';

const BUILD_HASH = process.env.BUILD_HASH;

const FETCH_LOCAL_CONFIG_ERROR = {
    BASE_ERROR: 'fetch local base config failed',
    LANG_ERROR: 'fetch local lang config failed',
};

export type Options = {
    brandId: string;
    lang: string;
};
export class LocalBrandConfigManager {
    public config: Partial<BrandConfig> = {};

    private brandId = '';

    private lang = 'en-US';

    private serverUrl = '';

    private static FALLBACK_LOCALE = 'en-US';

    constructor(options: Options) {
        this.brandId = options.brandId;
        this.lang = getLocale(options.lang);
        this.serverUrl = `${this.getLoadPath()}/configs/${this.brandId}`;
    }

    private getLoadPath() {
        const pathToStatic = `${BASE_ROOT}static`;

        return `${pathToStatic.replace(/\/+$/, '')}/dynamic_brand`;
    }

    private addBrandBaseUrl(url: string) {
        return url ? `${this.getLoadPath()}${url}` : '';
    }

    /**
     * fetch base config from local
     */
    async fetchLocalBaseConfig(): Promise<Partial<BrandConfig>> {
        const response = await fetch(`${this.serverUrl}/base.${BUILD_HASH}.json`, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(FETCH_LOCAL_CONFIG_ERROR.BASE_ERROR);
        }

        const data = await response.json();
        const assets = {
            ...data.assets,
            mainLogoUrl: this.addBrandBaseUrl(data.assets.mainLogoUrl),
            faviconUrl: this.addBrandBaseUrl(data.assets.faviconUrl),
            blackLogoUrl: this.addBrandBaseUrl(data.assets.blackLogoUrl),
            topPanelLogoUrl: this.addBrandBaseUrl(data.assets.topPanelLogoUrl),
            watermarkUrl: this.addBrandBaseUrl(data.assets.watermarkUrl),
            poweredByLogoUrl: this.addBrandBaseUrl(data.assets.poweredByLogoUrl),
            videoAboutIcon: this.addBrandBaseUrl(data.assets.videoAboutIcon),
        };
        return { ...data, assets };
    }

    /**
     * fetch lang config from local
     */
    async fetchLocalLangConfig(): Promise<Partial<BrandConfig>> {
        const buildLocalConfigUrl = (lang: string) =>
            `${this.serverUrl}/${lang}.${BUILD_HASH}.json`;

        let response = await fetch(buildLocalConfigUrl(this.lang), {
            credentials: 'include',
        });

        if (response.status === 404) {
            // if the current locale config is not found, fetch the fallback locale config
            response = await fetch(buildLocalConfigUrl(LocalBrandConfigManager.FALLBACK_LOCALE), {
                credentials: 'include',
            });
        }

        if (!response.ok) {
            throw new Error(FETCH_LOCAL_CONFIG_ERROR.LANG_ERROR);
        }

        const data = await response.json();
        return data;
    }

    async fetchLocalConfig() {
        const [baseConfig, langConfig] = await Promise.all([
            this.fetchLocalBaseConfig(),
            this.fetchLocalLangConfig(),
        ]);
        this.config = {
            ...this.config,
            ...baseConfig,
            ...langConfig,
        };
    }

    getConfig() {
        return this.config;
    }
}
